"use client";

import { useEffect, useState } from "react";
import {
  FEEDBACK_CATEGORIES,
  type FeedbackCategory,
  type FeedbackCaptureResult,
} from "@mcblessing1/in-app-feedback";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/utils";

const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  bug: "Report a problem",
  idea: "Suggest an idea",
  love: "Tell us what you love",
  other: "Something else",
};

type ProductFeedbackFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (input: {
    category: FeedbackCategory;
    primary: string;
    secondary: string;
    anythingElse: string;
  }) => FeedbackCaptureResult;
  onShown: () => void;
  onDismissed: () => void;
};

/**
 * Fresh instance per open (parent remounts via `key`).
 * Initial field state is default — no reset effect.
 */
export default function ProductFeedbackFormModal({
  isOpen,
  onClose,
  onSubmit,
  onShown,
  onDismissed,
}: ProductFeedbackFormModalProps) {
  const [category, setCategory] = useState<FeedbackCategory>("bug");
  const [primary, setPrimary] = useState("");
  const [secondary, setSecondary] = useState("");
  const [anythingElse, setAnythingElse] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const secondaryRequired = category === "bug" || category === "idea";

  useEffect(() => {
    if (!isOpen) return;
    onShown();
  }, [isOpen, onShown]);

  const handleClose = () => {
    if (!success) {
      onDismissed();
    }
    onClose();
  };

  const handleSubmit = () => {
    setSubmitting(true);
    setError(null);
    const result = onSubmit({
      category,
      primary,
      secondary,
      anythingElse,
    });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setSuccess(true);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={success ? "Thanks" : "Send feedback"}
      maxWidth="sm"
      footer={
        success ? (
          <Button
            type="button"
            variant="primary"
            className="w-full"
            onClick={onClose}
          >
            Done
          </Button>
        ) : (
          <div className="flex w-full gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              className="flex-1"
              onClick={handleSubmit}
              isLoading={submitting}
            >
              Send
            </Button>
          </div>
        )
      }
    >
      {success ? (
        <p className="text-sm text-text-light">
          Your feedback was sent. We use it to improve Sell It Off.
        </p>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="mb-2 text-sm font-medium text-foreground">
              What kind of feedback is this?
            </p>
            <div className="grid grid-cols-2 gap-2">
              {FEEDBACK_CATEGORIES.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCategory(key)}
                  className={cn(
                    "rounded-xl border px-3 py-2 text-sm font-medium transition-colors",
                    category === key
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-surface text-foreground hover:border-primary/40",
                  )}
                >
                  {CATEGORY_LABELS[key]}
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-foreground">
              {category === "bug"
                ? "What went wrong?"
                : category === "idea"
                  ? "What’s your idea?"
                  : category === "love"
                    ? "What do you love?"
                    : "What’s on your mind?"}
            </span>
            <textarea
              value={primary}
              onChange={(e) => setPrimary(e.target.value)}
              rows={3}
              className="w-full rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] px-4 py-2.5 text-[var(--input-text)] placeholder:text-[var(--input-placeholder)] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--input-focus-ring)]"
              placeholder="Be as specific as you can"
            />
          </label>

          {secondaryRequired ? (
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-foreground">
                {category === "idea"
                  ? "What are you trying to accomplish, and why would it help?"
                  : "What were you trying to do when it went wrong?"}
              </span>
              <textarea
                value={secondary}
                onChange={(e) => setSecondary(e.target.value)}
                rows={3}
                className="w-full rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] px-4 py-2.5 text-[var(--input-text)] placeholder:text-[var(--input-placeholder)] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--input-focus-ring)]"
              />
            </label>
          ) : null}

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-foreground">
              Anything else?{" "}
              <span className="font-normal text-text-light">(optional)</span>
            </span>
            <textarea
              value={anythingElse}
              onChange={(e) => setAnythingElse(e.target.value)}
              rows={2}
              className="w-full rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] px-4 py-2.5 text-[var(--input-text)] placeholder:text-[var(--input-placeholder)] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--input-focus-ring)]"
            />
          </label>

          {error ? <p className="text-sm text-red-600">{error}</p> : null}
        </div>
      )}
    </Modal>
  );
}
